import { Braces, Check, History, List, Loader2, Settings2, Workflow } from "lucide-react"
import { type RowAction, RowActionsMenu } from "@/shared/ui/row-actions-menu"
import { SegmentedControl } from "@/shared/ui/segmented-control"

export const PANEL_CLASS = "shrink-0 overflow-hidden rounded-2xl border border-border/60 bg-card"

export type FlowView = "list" | "canvas"

export function SaveIndicator({ isSaving }: { isSaving: boolean }) {
	return (
		<span className="inline-flex shrink-0 items-center gap-1" aria-live="polite">
			{isSaving ? (
				<>
					<Loader2 className="h-3 w-3 animate-spin" />
					<span className="max-sm:sr-only">сохраняем…</span>
				</>
			) : (
				<>
					<Check className="h-3 w-3" />
					<span className="max-sm:sr-only">сохранено</span>
				</>
			)}
		</span>
	)
}

/**
 * Редкие действия со сценарием: в меню, чтобы шапка не превращалась в ряд одинаковых кнопок.
 * `hideVariables`/`hideSettings` скрывают пункт там, где для него уже есть отдельная видимая
 * точка входа в шапке (кнопка «Переменные», клик по заголовку), чтобы не дублировать её тут же.
 */
export function MoreMenu({
	onPick,
	extra = [],
	hideVariables,
	hideSettings,
}: {
	onPick: (dialog: "variables" | "settings" | "versions") => void
	extra?: RowAction[]
	hideVariables?: boolean
	hideSettings?: boolean
}) {
	return (
		<RowActionsMenu
			label="Ещё"
			contentClassName="w-60"
			actions={[
				...extra,
				{
					label: "Переменные",
					icon: Braces,
					hidden: hideVariables,
					separatorBefore: extra.length > 0,
					onSelect: () => onPick("variables"),
				},
				{ label: "История версий", icon: History, onSelect: () => onPick("versions") },
				{
					label: "Настройки сценария",
					icon: Settings2,
					hidden: hideSettings,
					onSelect: () => onPick("settings"),
				},
			]}
		/>
	)
}

const VIEW_OPTIONS = [
	{
		value: "list",
		label: (
			<span className="flex items-center gap-1.5">
				<List className="size-4" />
				Список
			</span>
		),
	},
	{
		value: "canvas",
		label: (
			<span className="flex items-center gap-1.5">
				<Workflow className="size-4" />
				Схема
			</span>
		),
	},
] as const

/** Поток списком или схемой: в шапке, чтобы не занимать место на холсте. */
export function ViewSwitch({
	view,
	onChange,
}: {
	view: FlowView
	onChange: (view: FlowView) => void
}) {
	return (
		<SegmentedControl
			aria-label="Вид воронки"
			options={VIEW_OPTIONS}
			value={view}
			onChange={(next) => next && onChange(next)}
		/>
	)
}
